import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useT } from '../i18n/I18nProvider';
import { Banner } from './states';

/**
 * Downloads a redacted operational snapshot of the tenant for support and
 * troubleshooting. Visible only to tenant admins (the backend enforces it too). The
 * bundle contains no secrets by construction.
 */
export function SupportBundlePanel(): JSX.Element {
  const t = useT();
  const { can } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!can('tenant:admin')) return <></>;

  const download = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const bundle = await api.get<Record<string, unknown>>('/operations/support-bundle');
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `connaro-support-bundle-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>{t('support.title')}</h2>
      <p>{t('support.intro')}</p>
      {error ? <Banner variant="error">{error}</Banner> : null}
      <button type="button" className="btn" disabled={busy} onClick={() => void download()}>
        {busy ? t('support.generating') : t('support.download')}
      </button>
    </div>
  );
}
